import { useRef } from "react";
import { Paperclip, Upload, X } from "lucide-react";
import type { LimitesAnexo } from "../../../types/portalCliente";
import { formatarTamanho } from "./severidade";

interface SeletorAnexosProps {
  anexos: File[];
  aoAdicionar: (arquivos: File[]) => void;
  aoRemover: (indice: number) => void;
  limites: LimitesAnexo;
  arrastando?: boolean;
  desabilitado?: boolean;
}

export const SeletorAnexos = ({
  anexos,
  aoAdicionar,
  aoRemover,
  limites,
  arrastando = false,
  desabilitado = false,
}: SeletorAnexosProps) => {
  const entrada = useRef<HTMLInputElement>(null);

  const total = anexos.reduce((soma, a) => soma + a.size, 0);

  return (
    <div className="space-y-3">
      <button
        type="button"
        onClick={() => entrada.current?.click()}
        disabled={desabilitado}
        className={[
          "w-full rounded-xl border-2 border-dashed px-4 py-6 text-center transition-colors",
          arrastando
            ? "border-blue-500 bg-blue-50"
            : "border-gray-300 hover:border-blue-400 hover:bg-blue-50/40",
          desabilitado ? "cursor-not-allowed opacity-50" : "",
        ].join(" ")}
      >
        <Upload className="mx-auto mb-2 h-5 w-5 text-gray-400" />
        <span className="block text-sm font-medium text-gray-700">
          Escolher arquivos, arrastar para cá ou colar com Ctrl+V
        </span>
        <span className="mt-1 block text-xs text-gray-500">
          Até {limites.maxArquivos} arquivos,{" "}
          {formatarTamanho(limites.maxBytesPorArquivo)} cada e{" "}
          {formatarTamanho(limites.maxBytesTotal)} no total
        </span>
      </button>

      <input
        ref={entrada}
        type="file"
        multiple
        className="hidden"
        onChange={(e) => {
          aoAdicionar(Array.from(e.target.files ?? []));
          // Sem isto, escolher o mesmo arquivo de novo depois de removê-lo não
          // dispara change: o valor do input continuaria o mesmo.
          e.target.value = "";
        }}
      />

      {anexos.length > 0 && (
        <ul className="space-y-1">
          {anexos.map((arquivo, indice) => (
            <li
              key={`${arquivo.name}-${arquivo.size}`}
              className="flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm"
            >
              <Paperclip className="h-4 w-4 shrink-0 text-gray-400" />
              <span className="min-w-0 flex-1 truncate text-gray-700">
                {arquivo.name}
              </span>
              <span className="shrink-0 text-xs text-gray-500">
                {formatarTamanho(arquivo.size)}
              </span>
              <button
                type="button"
                onClick={() => aoRemover(indice)}
                aria-label={`Remover ${arquivo.name}`}
                className="shrink-0 rounded-full p-1 text-gray-400 transition-colors hover:bg-red-50 hover:text-red-600"
              >
                <X className="h-4 w-4" />
              </button>
            </li>
          ))}
          <li className="px-1 pt-1 text-xs text-gray-500">
            {anexos.length} arquivo(s), {formatarTamanho(total)} no total
          </li>
        </ul>
      )}
    </div>
  );
};

export default SeletorAnexos;
